import React, { useState, useEffect, useRef } from "react";
import {
  SMCmainDiv,
  SpeakingMicCardDiv,
  MicRoundBackground,
  DoneText,
  PlayRecordingCardDiv,
  PlayRecordingCardText,
  RecordingAndVisualizingDiv,
  MicInputDiv,
  WaveformContainer,
  AudioPlayer as CustomAudio,
  UseHeadSetDiv,
  UseHeadSetText,
  RecordingSliderDiv,
  RecordingSlider,
} from "./styles";
import MicImage from "../../assets/images/Mic.svg";
import WaveSurfer from "wavesurfer.js";
import InfoIcon from "../../assets/images/info-icon-3.svg";
import { FlexDiv } from "../../assets/styles/style";
import { useMediaQuery } from "@mui/material";
import Typography from "@mui/material/Typography";
import ReactAudioPlayer from "react-audio-player";

// Import utilities from pte-mock-test
import { playStartBeep, playEndBeep, formatTime } from "../../pte-mock-test/utils/audio";
import useWavRecorder from "../../pte-mock-test/hooks/speaking/useWavRecorder";

const SpeakingMicCard = ({
  setSelectedAnswers,
  resetTrigger,
  setCanSubmit,
  setRecordedAudio,
  setRecordedWavFile,
  autoStartRecording = false,
  resetAutoStart,
  handleRecordingStart,
  handleRecordingStop,
  totalTestTime,
  isAudioCompleted = false,
  isAudioPlayerComponent = false,
  isReTellLecture = false,
  postAudioPlayTestCountdownTime,
}) => {
  const isLaptop = useMediaQuery("(max-width:1100px)");
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [showWaveform, setShowWaveform] = useState(false);
  const audioRef = useRef(new Audio());
  const canvasRef = useRef(null);
  const waveformRef = useRef(null);
  const [transcript, setTranscript] = useState("");
  const recordingTimeoutRef = useRef(null);
  const startDelayTimeoutRef = useRef(null);
  const [isRecordingHasCompleted, setIsRecordingHasCompleted] = useState(false);
  const lastClickRef = useRef(Date.now());
  const [currentRecordingTime, setCurrentRecordingTime] = useState(0);
  const maxRecordingTime = totalTestTime;

  // New refs for enhanced functionality
  const recognitionRef = useRef(null);
  const liveTranscriptRef = useRef("");
  const speechRestartTimeoutRef = useRef(null);
  const mountedRef = useRef(false);
  const recognitionSessionIdRef = useRef(0);
  const isRecordingRef = useRef(false);
  
  // Old visualizer refs
  const analyserRef = useRef(null);
  const animationRef = useRef(null);

  // Initialize WAV recorder from pte-mock-test
  const wavRecorder = useWavRecorder({ targetSampleRate: 16000 });

  useEffect(() => {
    const shouldStartRecording = isAudioPlayerComponent ? autoStartRecording && isAudioCompleted : autoStartRecording;

    if (shouldStartRecording) {
      const timeoutId = setTimeout(() => {
        if (autoStartRecording) {
          startRecording();
        }
      }, 200);
      
      return () => {
        clearTimeout(timeoutId);
        resetAutoStart();
      };
    }
    return () => {
      resetAutoStart();
    };
  }, [autoStartRecording, isAudioCompleted]);

  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      try { if (speechRestartTimeoutRef.current) { clearTimeout(speechRestartTimeoutRef.current); speechRestartTimeoutRef.current = null; } } catch {}
      try {
        if (recognitionRef.current) {
          recognitionRef.current.onend = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.stop();
          recognitionRef.current = null;
        }
      } catch {}
      try {
        if (audioRef?.current) {
          audioRef.current.onloadedmetadata = null;
          audioRef.current.onended = null;
          audioRef.current.ontimeupdate = null;
          audioRef.current.pause?.();
        }
      } catch {}
    };
  }, []);

  useEffect(() => {
    setSelectedAnswers(transcript);
  }, [transcript]);

  useEffect(() => {
    const stoppingTheRecording = async () => {
      if (isRecording) {
        await stopRecording();
      }
    };

    const handleReset = async () => {
      await stoppingTheRecording();

      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
        recordingTimeoutRef.current = null;
      }

      if (startDelayTimeoutRef.current) {
        clearTimeout(startDelayTimeoutRef.current);
        startDelayTimeoutRef.current = null;
      }

      // Cleanup speech recognition
      try {
        if (speechRestartTimeoutRef.current) { 
          clearTimeout(speechRestartTimeoutRef.current); 
          speechRestartTimeoutRef.current = null; 
        }
        if (recognitionRef.current) { 
          recognitionRef.current.onend = null; 
          recognitionRef.current.onerror = null; 
          recognitionRef.current.stop(); 
          recognitionRef.current = null; 
        }
      } catch {}

      // Cleanup visualizer
      try { if (animationRef.current) cancelAnimationFrame(animationRef.current); } catch {}

      resetAutoStart();
      setRecordedAudio("");
      setAudioUrl("");
      setTranscript("");
      setShowWaveform(false);
      setIsRecordingHasCompleted(false);
      liveTranscriptRef.current = "";

      if (waveformRef.current) {
        waveformRef.current.destroy();
        waveformRef.current = null;
      }

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current.load();
      }

      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext("2d");
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    };

    handleReset();
  }, [resetTrigger]);

  useEffect(() => {
    return () => {
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }

      if (startDelayTimeoutRef.current) {
        clearTimeout(startDelayTimeoutRef.current);
      }

      // Cleanup speech recognition
      try {
        if (speechRestartTimeoutRef.current) { 
          clearTimeout(speechRestartTimeoutRef.current); 
        }
        if (recognitionRef.current) { 
          recognitionRef.current.onend = null; 
          recognitionRef.current.onerror = null; 
          recognitionRef.current.stop(); 
        }
      } catch {}

      // Cleanup visualizer
      try { if (animationRef.current) cancelAnimationFrame(animationRef.current); } catch {}
    };
  }, []);

  useEffect(() => {
    if (showWaveform && audioUrl) {
      waveformRef.current = WaveSurfer.create({
        container: "#waveform",
        waveColor: "grey",
        progressColor: "#996cfe",
        backgroundColor: "transparent",
        height: 100,
        barWidth: 0,
        cursorWidth: 1,
        cursorColor: "black",
        responsive: true,
        hideScrollbar: true,
        normalize: true,
      });

      waveformRef.current.load(audioUrl);
      waveformRef.current.setVolume(0);

      const playHandler = () => waveformRef.current.play();
      const pauseHandler = () => waveformRef.current.pause();
      const seekedHandler = () => {
        if (audioRef.current && waveformRef.current) {
          waveformRef.current.seekTo(
            audioRef.current.currentTime / audioRef.current.duration
          );
        }
      };

      audioRef.current.addEventListener("play", playHandler);
      audioRef.current.addEventListener("pause", pauseHandler);
      audioRef.current.addEventListener("seeked", seekedHandler);

      return () => {
        if (waveformRef.current) {
          waveformRef.current.destroy();
          waveformRef.current = null;
        }

        if (audioRef.current) {
          audioRef.current.removeEventListener("play", playHandler);
          audioRef.current.removeEventListener("pause", pauseHandler);
          audioRef.current.removeEventListener("seeked", seekedHandler);
        }
      };
    }
  }, [showWaveform, audioUrl]);

  useEffect(() => {
    if (audioUrl) {
      setRecordedAudio(audioUrl);
      if (waveformRef.current) {
        waveformRef.current.load(audioUrl);
      }
    }
  }, [audioUrl]);

  const startLiveRecognition = () => {
    if (!('webkitSpeechRecognition' in window)) return;
    if (!mountedRef.current || !isRecordingRef.current) return;
    try {
      const recId = ++recognitionSessionIdRef.current;
      const SpeechRecognition = window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event) => {
        if (recId !== recognitionSessionIdRef.current) return;
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            const chunk = event.results[i][0].transcript;
            liveTranscriptRef.current += chunk + ' ';
            setTranscript((prevTranscript) => prevTranscript + chunk + " ");
          }
        }
      };

      recognition.onerror = (e) => {
        if (recId !== recognitionSessionIdRef.current) return;
        console.warn('[Speech] onerror:', e?.error || e);
        // If no-speech or network hiccup, try to restart quickly while recording
        if (mountedRef.current && isRecordingRef.current) {
          if (speechRestartTimeoutRef.current) clearTimeout(speechRestartTimeoutRef.current);
          speechRestartTimeoutRef.current = setTimeout(() => {
            try { recognitionRef.current && recognitionRef.current.stop(); } catch {}
            if (!mountedRef.current || !isRecordingRef.current) return;
            startLiveRecognition();
          }, 150);
        }
      };

      recognition.onend = () => {
        if (recId !== recognitionSessionIdRef.current) return;
        if (mountedRef.current && isRecordingRef.current) {
          if (speechRestartTimeoutRef.current) clearTimeout(speechRestartTimeoutRef.current);
          speechRestartTimeoutRef.current = setTimeout(() => {
            if (!mountedRef.current || !isRecordingRef.current) return;
            startLiveRecognition();
          }, 100);
        }
      };

      recognition.start();
      recognitionRef.current = recognition;
    } catch {}
  };

  const startRecording = async (cancelDelay = false) => {
    if (
      isRecording ||
      isRecordingHasCompleted ||
      recognitionRef.current
    ) {
      console.log("Recording or speech recognition already in progress.");
      return;
    }

    if (isReTellLecture && !cancelDelay) {
      startDelayTimeoutRef.current = setTimeout(() => {
        startRecordingProcess();
      }, postAudioPlayTestCountdownTime);
    } else {
      clearTimeout(startDelayTimeoutRef.current);
      startRecordingProcess();
    }
  };

  const startRecordingProcess = async () => {
    await playStartBeep();
    try {
      try { setTranscript(""); } catch {}
      const { stream } = await wavRecorder.start();

      setIsRecording(true);
      isRecordingRef.current = true;
      setIsRecordingHasCompleted(false);
      handleRecordingStart();

      // Old visualizer setup
      const audioContext = new window.AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      analyserRef.current = audioContext.createAnalyser();
      source.connect(analyserRef.current);

      // Live transcription
      liveTranscriptRef.current = '';
      startLiveRecognition();

      recordingTimeoutRef.current = setTimeout(() => {
        console.log("Total test time passed, automatically stopping the recording.");
        stopRecording();
      }, totalTestTime);

      draw();
      setCanSubmit(false);
    } catch (err) {
      console.error("Failed to start recorder:", err);
    }
  };

  const stopRecording = async () => {
    if (!isRecordingRef.current) return;
    
    setIsRecording(false);
    isRecordingRef.current = false;
    clearTimeout(recordingTimeoutRef.current);
    recordingTimeoutRef.current = null;

    // Play end beep
    await playEndBeep();

    try {
      if (speechRestartTimeoutRef.current) { clearTimeout(speechRestartTimeoutRef.current); speechRestartTimeoutRef.current = null; }
      if (recognitionRef.current) { recognitionRef.current.onend = null; recognitionRef.current.onerror = null; recognitionRef.current.stop(); recognitionRef.current = null; }
    } catch {}

    // Stop WAV recorder and get blob
    try {
      const result = await wavRecorder.stop();
      if (result && result.blob) {
        const file = new File([result.blob], 'recording.wav', { type: 'audio/wav', lastModified: Date.now() });
        setRecordedWavFile(file);
        
        const audioUrl = URL.createObjectURL(result.blob);
        setAudioUrl(audioUrl);

        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          setShowWaveform(true);
        }

        setCanSubmit(true);
        console.log("Recording stopped and processed.");
      }
    } catch (e) {
      console.error('WAV stop failed:', e);
    }

    setIsRecordingHasCompleted(true);
    handleRecordingStop();

    // Finalize transcription
    const finalText = (liveTranscriptRef.current || '').trim();
    if (finalText) {
      setTranscript(finalText);
    }
  };

  const draw = () => {
    if (!canvasRef.current || !analyserRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    const barWidth = (width / 255) * 2.5;
    const maxHeight = height * 0.7;
    let barHeight;
    let x = 0;

    analyserRef.current.fftSize = 256;
    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const renderFrame = () => {
      x = 0;
      analyserRef.current.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < bufferLength; i++) {
        barHeight = (dataArray[i] / 255) * maxHeight;

        const r = barHeight + 25 * (i / bufferLength);
        const g = 250 * (i / bufferLength);
        const b = 50;

        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.fillRect(x, height - barHeight, barWidth, barHeight);

        x += barWidth + 1;
      }
      animationRef.current = requestAnimationFrame(renderFrame);
    };
    renderFrame();
  };

  const toggleRecording = async () => {
    if (Date.now() - lastClickRef.current < 1500) return;
    lastClickRef.current = Date.now();

    if (isRecording) {
      await stopRecording();
    } else {
      await startRecording(true);
    }
  };

  useEffect(() => {
    let intervalId;
    if (isRecording) {
      intervalId = setInterval(() => {
        setCurrentRecordingTime((prevTime) => {
          const newTime = prevTime + 100;
          if (newTime <= maxRecordingTime) {
            return newTime;
          } else {
            clearInterval(intervalId);
            return maxRecordingTime;
          }
        });
      }, 100);
    } else {
      setCurrentRecordingTime(0);
    }

    return () => clearInterval(intervalId);
  }, [isRecording, maxRecordingTime]);


  return (
    <>
      <SMCmainDiv>
        <SpeakingMicCardDiv>
          {showWaveform ? (
            <WaveformContainer id="waveform" />
          ) : (
            <RecordingAndVisualizingDiv>
              <DoneText>
                {isRecording ? "Recording" : audioUrl ? "Done" : "Start"}
              </DoneText>
              <MicRoundBackground onClick={toggleRecording}>
                <img
                  src={MicImage}
                  alt={isRecording ? "Stop Recording" : "Start Recording"}
                />
              </MicRoundBackground>
              {!isRecording && (
                <FlexDiv
                  style={{
                    width: "100%",
                    justifyContent: isLaptop ? "" : "flex-end",
                    marginTop: isLaptop ? "" : "-2rem",
                  }}
                >
                  <UseHeadSetDiv isRecording={isRecording}>
                    <img src={InfoIcon} alt="" />
                    <UseHeadSetText>
                      Use a headset with inline microphone to get accurate AI
                      scores
                    </UseHeadSetText>
                  </UseHeadSetDiv>
                </FlexDiv>
              )}

              <MicInputDiv>
                <canvas ref={canvasRef} width="30" height="130"></canvas>
              </MicInputDiv>

              {isRecording && (
                <RecordingSliderDiv>
                  <Typography variant="body2">
                    {formatTime(currentRecordingTime / 1000)}
                  </Typography>
                  <RecordingSlider
                    value={currentRecordingTime}
                    min={0}
                    step={100}
                    max={maxRecordingTime}
                    aria-labelledby="continuous-slider"
                    valueLabelDisplay="off"
                    sx={{ mx: 2, flex: 1, my: "-4.5px" }}
                  />
                  <Typography variant="body2">
                    {formatTime(maxRecordingTime / 1000)}
                  </Typography>
                </RecordingSliderDiv>
              )}
            </RecordingAndVisualizingDiv>
          )}
        </SpeakingMicCardDiv>

        <PlayRecordingCardDiv>
          <CustomAudio style={{ display: audioUrl ? "block" : "none" }}>
            <div style={{marginTop: '5px'}}>
              <audio controls ref={audioRef}>
                <source src={audioUrl} type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
            </div>
          </CustomAudio>
          <PlayRecordingCardText>
            AI Scoring and Audio Answer is available after submission.
          </PlayRecordingCardText>
        </PlayRecordingCardDiv>
      </SMCmainDiv>
    </>
  );
};

export default SpeakingMicCard;
